import { makeAutoObservable, observable, action } from "mobx";
import { v4 } from "uuid";

import { Operation, VariableSet } from "./types";

class OperationStore {
  @observable private _operations = new Map<string, Operation>();

  constructor() {
    makeAutoObservable(this);
  }

  @action
  createNewOperation({ input_variable_sets }: { input_variable_sets: VariableSet[] }) {
    const operationId = v4();
    const sets = input_variable_sets.reduce(
      (acc, set) => ({ ...acc, [set.id]: set }),
      {} as Record<string, VariableSet>,
    );

    this._operations.set(operationId, { id: operationId, sets });

    return operationId;
  }

  @action
  markCancelRequested(operationIds: string[]) {
    operationIds.forEach(operationId => {
      const operation = this.getOperation(operationId);
      if (operation) {
        operation.cancel_requested = true;
      }
    });
  }

  @action
  clearCancelRequested(operationIds: string[]) {
    operationIds.forEach(operationId => {
      const operation = this.getOperation(operationId);
      if (operation) {
        operation.cancel_requested = false;
      }
    });
  }

  get getAssociatedMessageId() {
    return (operation_id: string, request_id: number) => {
      const operation = this.getOperation(operation_id);
      if (!operation) {
        return null;
      }
      const messageId = Object.values(operation.sets).find(
        set => set.request_id === request_id,
      )?.message_id;
      return messageId;
    };
  }

  get ongoingOperations() {
    return Array.from(this._operations.values()).filter(operation => !operation.cancel_requested)
      .length;
  }

  get isRunning() {
    return this.ongoingOperations >= 1;
  }

  /** Check if a message belongs to any active (in-flight) operation. */
  isMessageInActiveOperation(message_id: string): boolean {
    for (const operation of this._operations.values()) {
      if (operation.cancel_requested) {
        continue;
      }
      for (const set of Object.values(operation.sets)) {
        if (set?.message_id === message_id) {
          return true;
        }
      }
    }
    return false;
  }

  get getOperation() {
    return (operation_id: string) => {
      const operation = this._operations.get(operation_id);
      return operation;
    };
  }

  get getOperations() {
    return () => this._operations;
  }

  getCancellationTargets(operationIds = Array.from(this._operations.keys())) {
    const operations = operationIds
      .map(operationId => this.getOperation(operationId))
      .filter((operation): operation is Operation => Boolean(operation));

    return {
      runGroupIds: operations.map(operation => operation.id),
      individualRunRequestIds: operations.flatMap(operation =>
        Object.values(operation.sets)
          .map(set => set.request_id)
          .filter((requestId): requestId is number => typeof requestId === "number"),
      ),
    };
  }

  get isCancelRequested() {
    return (operation_id: string) => Boolean(this.getOperation(operation_id)?.cancel_requested);
  }

  @action
  deleteOperation(operation_id: string) {
    this._operations.delete(operation_id);
  }

  findSetByRequestId(request_id: number): { operation: Operation; setId: string } | null {
    for (const operation of this._operations.values()) {
      const matchingSet = Object.entries(operation.sets).find(
        ([, set]) => set.request_id === request_id,
      );
      if (matchingSet) {
        return { operation, setId: matchingSet[0] };
      }
    }
    return null;
  }

  get getAssociatedOperationByOutputId() {
    return (output_id: string) => {
      const res: [string, Operation] | undefined = Array.from(this._operations.entries()).find(
        ([, value]: [string, Operation]) => value?.output_instance_id === output_id,
      );

      return res?.[1] as Operation | undefined;
    };
  }

  get getAssociatedOperationByRequestId() {
    return (request_id: number) => this.findSetByRequestId(request_id)?.operation;
  }

  @action
  assignOutputId(operation_id: string, output_id: string) {
    const operation = this.getOperation(operation_id);
    if (!operation) {
      return;
    }
    operation.output_instance_id = output_id;
  }

  get getAssociatedSetId() {
    return (request_id: number) => this.findSetByRequestId(request_id)?.operation.id;
  }

  @action
  assignError(operation_id: string, request_id: number, error: string) {
    const operation = this.getOperation(operation_id);
    if (!operation) {
      return;
    }

    const matchingSet = this.findSetByRequestId(request_id);

    if (!matchingSet || matchingSet.operation.id !== operation_id) {
      return;
    }

    const { setId } = matchingSet;
    if (operation.sets[setId]) {
      operation.sets[setId].error = error;
    }
  }

  @action
  assignMessageId(operation_id: string, message_id: string, request_id: number) {
    const operation = this.getOperation(operation_id);

    if (!operation) {
      return;
    }

    const foundSet = this.findSetByRequestId(request_id);
    if (!foundSet || foundSet.operation.id !== operation_id) {
      return;
    }
    operation.sets[foundSet.setId].message_id = message_id;
  }

  @action
  assignSetRequestId(operation_id: string, variable_set_id: string, request_id: number) {
    const operation = this.getOperation(operation_id);
    if (!operation) {
      return;
    }
    operation.sets[variable_set_id].request_id = request_id;
  }

  @action assignSetPosition(operation_id: string, variable_set_id: string, position: number) {
    const operation = this.getOperation(operation_id);
    if (!operation) {
      return;
    }
    operation.sets[variable_set_id].position = position;
  }
}

export default OperationStore;
